import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, apiFileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Truck, Fuel, Wrench, ClipboardCheck, Plus, Trash2, PencilLine, Save,
    Gauge, ShieldCheck, CalendarClock, AlertTriangle, Activity, Search,
    QrCode, Barcode, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCurrency, formatNumber } from "@/lib/format";
import { farmDateIso } from "@/lib/farmTime";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const VEHICLE_CATEGORIES = [
    { key: "vehicle", label: "Vehicles" },
    { key: "tractor", label: "Tractors" },
    { key: "motorcycle", label: "Motorcycles" },
    { key: "atv", label: "ATVs" },
    { key: "truck", label: "Trucks" },
    { key: "trailer", label: "Trailers" },
    { key: "boat", label: "Boats" },
    { key: "heavy_equipment", label: "Heavy Equipment" },
    { key: "custom_equipment", label: "Custom Equipment" },
];

const VEHICLE_STATUS = [
    { key: "available", label: "Available", color: "bg-emerald-500/15 text-emerald-700 border-emerald-500/40" },
    { key: "in_use", label: "In Use", color: "bg-primary/15 text-primary border-primary/40" },
    { key: "maintenance", label: "Maintenance", color: "bg-amber-500/15 text-amber-700 border-amber-500/40" },
    { key: "repair", label: "Repair", color: "bg-rose-500/15 text-rose-700 border-rose-500/40" },
    { key: "retired", label: "Retired", color: "bg-slate-500/15 text-slate-700 border-slate-500/40" },
    { key: "sold", label: "Sold", color: "bg-blue-500/15 text-blue-700 border-blue-500/40" },
];

const FUEL_TYPES = ["Diesel", "Gasoline", "LPG", "Electric", "Hybrid"];
const MAINT_KINDS = [
    { key: "service", label: "Service Schedule" },
    { key: "oil_change", label: "Oil Change" },
    { key: "filters", label: "Filters" },
    { key: "tyres", label: "Tyres" },
    { key: "battery", label: "Battery" },
    { key: "brakes", label: "Brakes" },
    { key: "coolant", label: "Coolant" },
    { key: "hydraulic_oil", label: "Hydraulic Oil" },
    { key: "repair", label: "Repair" },
];
const REPORT_TYPES = [
    { key: "service_report", label: "Service Report" },
    { key: "fuel_report", label: "Fuel Report" },
    { key: "cost_report", label: "Cost Report" },
    { key: "utilization_report", label: "Utilization Report" },
    { key: "breakdown_report", label: "Breakdown Report" },
    { key: "expense_report", label: "Expense Report" },
];

const EXPENSE_CATEGORIES = [
    { key: "fuel", label: "Fuel" },
    { key: "registration", label: "Registration" },
    { key: "insurance", label: "Insurance" },
    { key: "repairs", label: "Repairs" },
    { key: "parts", label: "Parts" },
    { key: "tires", label: "Tires" },
    { key: "battery", label: "Battery" },
    { key: "oil", label: "Oil" },
    { key: "labor", label: "Labor" },
    { key: "other", label: "Other" },
];

const statusMeta = (k) => VEHICLE_STATUS.find((s) => s.key === (k || "available")) || VEHICLE_STATUS[0];
const categoryLabel = (k) => VEHICLE_CATEGORIES.find((c) => c.key === k)?.label || "Custom Equipment";
const submissionId = (prefix) => `${prefix}-${farmDateIso().replace(/-/g, "")}-${crypto.randomUUID()}`;

function KpiCard({ icon: Icon, label, value, sub, tone = "primary", testid }) {
    const tones = {
        primary: "bg-primary/10 text-primary",
        amber: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
        rose: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
        emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
        slate: "bg-slate-500/15 text-slate-700 dark:text-slate-300",
        blue: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
    };
    return (
        <Card className="p-4" data-testid={testid}>
            <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl grid place-items-center ${tones[tone]}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground truncate">{label}</div>
                    <div className="text-xl font-bold leading-tight">{value}</div>
                </div>
            </div>
            {sub && <div className="text-[11px] text-muted-foreground mt-2">{sub}</div>}
        </Card>
    );
}

function VehicleDialog({ open, initial, employees, onSave, onClose }) {
    const [draft, setDraft] = useState({});
    useEffect(() => {
        if (open) {
            setDraft({
                status: "available",
                vehicle_category: "vehicle",
                photo_gallery: [],
                source_submission_id: initial?.source_submission_id || submissionId("fleet-vehicle"),
                ...(initial || {}),
            });
        }
    }, [open, initial?.id]);
    const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="vehicle-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5" />{draft.id ? "Edit Vehicle" : "New Fleet Unit"}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        ["vehicle_id", "Vehicle ID"],
                        ["name", "Unit Name"],
                        ["plate_no", "Plate Number"],
                        ["vin", "VIN"],
                        ["engine_no", "Engine Number"],
                        ["make", "Brand"],
                        ["model", "Model"],
                        ["year", "Year", "number"],
                        ["color", "Color"],
                        ["transmission", "Transmission"],
                        ["registration_number", "Registration Number"],
                        ["insurance_policy_no", "Insurance Policy No."],
                        ["current_odometer", "Odometer", "number"],
                        ["engine_hours", "Engine Hours", "number"],
                        ["assigned_farm", "Assigned Farm"],
                        ["barcode", "Barcode"],
                    ].map(([k, l, t]) => (
                        <div key={k}>
                            <Label className="text-xs">{l}</Label>
                            <Input
                                type={t || "text"}
                                value={draft[k] ?? ""}
                                className="mt-1 h-9"
                                onChange={(e) => upd(k, t === "number" ? Number(e.target.value) || null : e.target.value)}
                                data-testid={`veh-${k}`}
                            />
                        </div>
                    ))}
                    <div>
                        <Label className="text-xs">{i18n.t("final.a3c686e711e4")}</Label>
                        <Select value={draft.vehicle_category || "vehicle"} onValueChange={(v) => upd("vehicle_category", v)}>
                            <SelectTrigger className="mt-1 h-9" data-testid="veh-category"><SelectValue /></SelectTrigger>
                            <SelectContent>{VEHICLE_CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.3917cc63a2b1")}</Label>
                        <Select value={draft.fuel_type || "Diesel"} onValueChange={(v) => upd("fuel_type", v)}>
                            <SelectTrigger className="mt-1 h-9" data-testid="veh-fuel_type"><SelectValue /></SelectTrigger>
                            <SelectContent>{FUEL_TYPES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.bae7d5be7082")}</Label>
                        <Select value={draft.status || "available"} onValueChange={(v) => upd("status", v)}>
                            <SelectTrigger className="mt-1 h-9" data-testid="veh-status"><SelectValue /></SelectTrigger>
                            <SelectContent>{VEHICLE_STATUS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.527d428ee46b")}</Label>
                        <Select value={draft.assigned_driver_id || "__none__"} onValueChange={(v) => upd("assigned_driver_id", v === "__none__" ? null : v)}>
                            <SelectTrigger className="mt-1 h-9" data-testid="veh-driver"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">{i18n.t("final.c044647b0197")}</SelectItem>
                                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {[
                        ["registration_expiry", "Registration Expiry", "date"],
                        ["insurance_expiry", "Insurance Expiry", "date"],
                        ["next_service_date", "Next Service Date", "date"],
                    ].map(([k, l, t]) => (
                        <div key={k}>
                            <Label className="text-xs">{l}</Label>
                            <Input type={t} value={draft[k] ?? ""} className="mt-1 h-9" onChange={(e) => upd(k, e.target.value)} />
                        </div>
                    ))}
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">{i18n.t("final.40cd63f2ed77")}</Label>
                        <Textarea
                            rows={4}
                            value={Array.isArray(draft.photo_gallery) ? draft.photo_gallery.join("\n") : ""}
                            onChange={(e) => upd("photo_gallery", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.70440046a3dc")}</Label>
                        <Textarea rows={4} value={draft.notes ?? ""} onChange={(e) => upd("notes", e.target.value)} className="mt-1" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => onSave(draft)} data-testid="veh-save-btn"><Save className="w-4 h-4 mr-2" />{i18n.t("final.efc007a393f6")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FuelDialog({ open, initial, vehicles, onSave, onClose }) {
    const [draft, setDraft] = useState({});
    useEffect(() => {
        if (open) setDraft({ date: farmDateIso(), liters: "", price_per_l: "", mileage: "", engine_hours: "", source_submission_id: initial?.source_submission_id || submissionId("fleet-fuel"), ...(initial || {}) });
    }, [open, initial?.id]);
    const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    const totalPreview = ((Number(draft.liters) || 0) * (Number(draft.price_per_l) || 0)).toFixed(2);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="fuel-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Fuel className="w-5 h-5" />{i18n.t("final.b42abd2e5039")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{i18n.t("final.9bb28c0dd307")}</Label>
                        <Select value={draft.vehicle_id || ""} onValueChange={(v) => upd("vehicle_id", v)}>
                            <SelectTrigger className="mt-1 h-10" data-testid="fuel-vehicle"><SelectValue placeholder={i18n.t("final.1f77e83907c4")} /></SelectTrigger>
                            <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">{i18n.t("final.eb9a4bc1c0c1")}</Label><Input type="date" value={draft.date || ""} onChange={(e) => upd("date", e.target.value)} className="mt-1 h-10" data-testid="fuel-date" /></div>
                        <div><Label className="text-xs">{i18n.t("final.1de07a26ff24")}</Label><Input type="number" value={draft.odometer ?? ""} onChange={(e) => upd("odometer", Number(e.target.value) || null)} className="mt-1 h-10" /></div>
                        <div><Label className="text-xs">{i18n.t("final.2942e6ae9dc3")}</Label><Input type="number" value={draft.engine_hours ?? ""} onChange={(e) => upd("engine_hours", Number(e.target.value) || null)} className="mt-1 h-10" /></div>
                        <div><Label className="text-xs">{i18n.t("final.e7eccafdec41")}</Label><Input type="number" step="0.01" value={draft.liters ?? ""} onChange={(e) => upd("liters", Number(e.target.value) || 0)} className="mt-1 h-10" data-testid="fuel-liters" /></div>
                        <div><Label className="text-xs">{i18n.t("final.03a8b8a27e2a")}</Label><Input type="number" step="0.01" value={draft.price_per_l ?? ""} onChange={(e) => upd("price_per_l", Number(e.target.value) || 0)} className="mt-1 h-10" data-testid="fuel-price" /></div>
                        <div><Label className="text-xs">{i18n.t("final.4cb8d8545ed6")}</Label><Input value={draft.station ?? ""} onChange={(e) => upd("station", e.target.value)} className="mt-1 h-10" /></div>
                    </div>
                    <div className="text-sm text-muted-foreground">{i18n.t("final.d8e7170f94e7")} <span className="font-semibold text-foreground">{formatCurrency(totalPreview)}</span></div>
                    <Textarea rows={2} value={draft.notes ?? ""} onChange={(e) => upd("notes", e.target.value)} placeholder={i18n.t("final.70440046a3dc")} />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => onSave(draft)} disabled={!draft.vehicle_id || !draft.liters} data-testid="fuel-save-btn"><Save className="w-4 h-4 mr-2" />{draft.id ? "Save Changes" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function MaintDialog({ open, initial, vehicles, onSave, onClose }) {
    const [draft, setDraft] = useState({});
    useEffect(() => {
        if (open) setDraft({ date: farmDateIso(), kind: "service", cost: "", labor_cost: "", source_submission_id: initial?.source_submission_id || submissionId("fleet-maintenance"), ...(initial || {}) });
    }, [open, initial?.id]);
    const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="maint-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Wrench className="w-5 h-5" />{i18n.t("final.f6741ee79071")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{i18n.t("final.9bb28c0dd307")}</Label>
                        <Select value={draft.vehicle_id || ""} onValueChange={(v) => upd("vehicle_id", v)}>
                            <SelectTrigger className="mt-1 h-10" data-testid="maint-vehicle"><SelectValue placeholder={i18n.t("final.1f77e83907c4")} /></SelectTrigger>
                            <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">{i18n.t("final.3deb74565196")}</Label>
                            <Select value={draft.kind || "service"} onValueChange={(v) => upd("kind", v)}>
                                <SelectTrigger className="mt-1 h-10" data-testid="maint-kind"><SelectValue /></SelectTrigger>
                                <SelectContent>{MAINT_KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label className="text-xs">{i18n.t("final.eb9a4bc1c0c1")}</Label><Input type="date" value={draft.date || ""} onChange={(e) => upd("date", e.target.value)} className="mt-1 h-10" data-testid="maint-date" /></div>
                        <div><Label className="text-xs">{i18n.t("final.b5d8da40f3ed")}</Label><Input type="number" value={draft.cost ?? ""} onChange={(e) => upd("cost", Number(e.target.value) || 0)} className="mt-1 h-10" data-testid="maint-cost" /></div>
                        <div><Label className="text-xs">{i18n.t("final.002c6fe8026a")}</Label><Input type="number" value={draft.labor_cost ?? ""} onChange={(e) => upd("labor_cost", Number(e.target.value) || 0)} className="mt-1 h-10" /></div>
                        <div><Label className="text-xs">{i18n.t("final.71e7e5db4ac7")}</Label><Input type="date" value={draft.next_due_date || ""} onChange={(e) => upd("next_due_date", e.target.value)} className="mt-1 h-10" /></div>
                        <div><Label className="text-xs">{i18n.t("final.7ceee3f3615a")}</Label><Input value={draft.provider ?? ""} onChange={(e) => upd("provider", e.target.value)} className="mt-1 h-10" /></div>
                    </div>
                    <div><Label className="text-xs">{i18n.t("final.002bdbfcf96d")}</Label><Textarea rows={2} value={draft.parts_used ?? ""} onChange={(e) => upd("parts_used", e.target.value)} placeholder={i18n.t("final.4d4add04f200")} /></div>
                    <div><Label className="text-xs">{i18n.t("final.70440046a3dc")}</Label><Textarea rows={2} value={draft.notes ?? ""} onChange={(e) => upd("notes", e.target.value)} /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => onSave(draft)} disabled={!draft.vehicle_id} data-testid="maint-save-btn"><Save className="w-4 h-4 mr-2" />{draft.id ? "Save Changes" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ExpenseDialog({ open, initial, vehicles, onSave, onClose }) {
    const [draft, setDraft] = useState({});
    const [uploading, setUploading] = useState(false);
    useEffect(() => {
        if (open) {
            setDraft({
                date: farmDateIso(),
                source_submission_id: initial?.source_submission_id || submissionId("fleet-expense"),
                category: "fuel",
                amount: "",
                supplier: "",
                notes: "",
                ...(initial || {}),
            });
        }
    }, [open, initial?.id]);
    const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    const uploadReceipt = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const stored = await api.post("/upload", fd);
            const rec = stored.data || {};
            upd("receipt_file_id", rec.id || "");
            upd("receipt_filename", rec.original_filename || file.name);
            upd("receipt_url", rec.id ? apiFileUrl(rec.id) : "");
            upd("receipt_mime_type", rec.content_type || file.type || "");
        } finally {
            setUploading(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="expense-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />{i18n.t("final.b1951f9838fc")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">{i18n.t("final.eb9a4bc1c0c1")}</Label>
                        <Input type="date" value={draft.date || ""} onChange={(e) => upd("date", e.target.value)} className="mt-1 h-10" data-testid="expense-date" />
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.9bb28c0dd307")}</Label>
                        <Select value={draft.vehicle_id || ""} onValueChange={(v) => upd("vehicle_id", v)}>
                            <SelectTrigger className="mt-1 h-10" data-testid="expense-vehicle"><SelectValue placeholder={i18n.t("final.1f77e83907c4")} /></SelectTrigger>
                            <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.a3c686e711e4")}</Label>
                        <Select value={draft.category || "fuel"} onValueChange={(v) => upd("category", v)}>
                            <SelectTrigger className="mt-1 h-10" data-testid="expense-category"><SelectValue /></SelectTrigger>
                            <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.55edd462872a")}</Label>
                        <Input value={draft.supplier || ""} onChange={(e) => upd("supplier", e.target.value)} className="mt-1 h-10" />
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.43dc8532f7e5")}</Label>
                        <Input type="number" step="0.01" value={draft.amount ?? ""} onChange={(e) => upd("amount", Number(e.target.value) || 0)} className="mt-1 h-10" data-testid="expense-amount" />
                    </div>
                    <div>
                        <Label className="text-xs">{i18n.t("final.5bdc5634400e")}</Label>
                        <Input type="file" accept="image/*,application/pdf" className="mt-1 h-10" onChange={(e) => uploadReceipt(e.target.files?.[0])} />
                        <div className="text-[11px] text-muted-foreground mt-1">{uploading ? "Uploading receipt..." : draft.receipt_filename || "Optional"}</div>
                    </div>
                    <div className="col-span-2">
                        <Label className="text-xs">{i18n.t("final.96662245e860")}</Label>
                        <Input value={draft.receipt_url || ""} onChange={(e) => upd("receipt_url", e.target.value)} className="mt-1 h-10" placeholder={i18n.t("final.dd44e0c7f248")} />
                    </div>
                    <div className="col-span-2">
                        <Label className="text-xs">{i18n.t("final.70440046a3dc")}</Label>
                        <Textarea rows={3} value={draft.notes || ""} onChange={(e) => upd("notes", e.target.value)} className="mt-1" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => onSave(draft)} disabled={!draft.vehicle_id || !draft.amount || !draft.date} data-testid="expense-save-btn"><Save className="w-4 h-4 mr-2" />{draft.id ? "Save Changes" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function FleetPage() {
    const { t } = useTranslation();
    const { can } = useAuth();

    const [tab, setTab] = useState("dashboard");
    const [summary, setSummary] = useState({ kpis: {}, expiries: {}, fuel_trend: [], notifications: {} });
    const [vehicles, setVehicles] = useState([]);
    const [fuelLogs, setFuelLogs] = useState([]);
    const [maint, setMaint] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [inspectionTpls, setInspectionTpls] = useState([]);

    const [reportType, setReportType] = useState("service_report");
    const [reportRows, setReportRows] = useState([]);

    const [vehicleSearch, setVehicleSearch] = useState("");
    const [vehicleStatus, setVehicleStatus] = useState("all");
    const [vehicleCategory, setVehicleCategory] = useState("all");
    const [page, setPage] = useState(1);

    const [vehicleDlg, setVehicleDlg] = useState(null);
    const [fuelDlg, setFuelDlg] = useState(null);
    const [maintDlg, setMaintDlg] = useState(null);
    const [expenseDlg, setExpenseDlg] = useState(null);

    const pageSize = 12;

    const load = async () => {
        try {
            const [s, v, f, m, expensesResp, employeesResp, tpls] = await Promise.all([
                api.get("/fleet/summary"),
                api.get("/fleet/vehicles?limit=1000"),
                api.get("/fleet/fuel-logs?limit=500"),
                api.get("/fleet/maintenance?limit=500"),
                api.get("/fleet/expenses?limit=500"),
                api.get("/employees").catch(() => ({ data: [] })),
                api.get("/forms/templates?category=fleet_inspection"),
            ]);
            setSummary(s.data || { kpis: {} });
            setVehicles(v.data || []);
            setFuelLogs(f.data || []);
            setMaint(m.data || []);
            setExpenses(expensesResp.data || []);
            setEmployees(employeesResp.data || []);
            setInspectionTpls(tpls.data || []);
        } catch {
            toast.error("Failed to load fleet data");
        }
    };

    const loadReport = async (type = reportType) => {
        try {
            const { data } = await api.get(`/fleet/reports/${type}`);
            setReportRows(data?.rows || []);
        } catch {
            toast.error("Failed to load report");
            setReportRows([]);
        }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => { if (tab === "reports") loadReport(reportType); }, [tab, reportType]);

    const saveVehicle = async (v) => {
        try {
            const payload = {
                ...v,
                vehicle_id: v.vehicle_id || v.id || `V-${Date.now()}`,
                qr_code: v.qr_code || v.vehicle_id || v.id || `V-${Date.now()}`,
                barcode: v.barcode || v.vehicle_id || v.id || `V-${Date.now()}`,
                photo_gallery: Array.isArray(v.photo_gallery) ? v.photo_gallery : [],
            };
            if (v.id) await api.put(`/fleet/vehicles/${v.id}`, payload);
            else await api.post("/fleet/vehicles", payload);
            toast.success("Vehicle saved");
            setVehicleDlg(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const saveFuel = async (d) => {
        try {
            const payload = { ...d, total_cost: d.total_cost ?? (Number(d.liters || 0) * Number(d.price_per_l || 0)) };
            if (d.id) await api.put(`/fleet/fuel/${d.id}`, payload);
            else await api.post("/fleet/fuel", payload);
            toast.success("Fuel log saved");
            setFuelDlg(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const delVehicle = async (v) => {
        if (!window.confirm(`Delete ${v.name}?`)) return;
        try {
            await api.delete(`/fleet/vehicles/${v.id}`);
            toast.success("Deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const delFuel = async (row) => {
        if (!window.confirm("Delete this fuel log?")) return;
        try {
            await api.delete(`/fleet/fuel/${row.id}`);
            toast.success("Fuel log deleted");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const saveMaint = async (d) => {
        try {
            if (d.id) await api.put(`/fleet/maintenance/${d.id}`, d);
            else await api.post("/fleet/maintenance", d);
            toast.success("Maintenance saved");
            setMaintDlg(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const delMaint = async (row) => {
        if (!window.confirm("Delete this maintenance record?")) return;
        try {
            await api.delete(`/fleet/maintenance/${row.id}`);
            toast.success("Maintenance deleted");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const saveExpense = async (d) => {
        try {
            if (d.id) await api.put(`/fleet/expenses/${d.id}`, d);
            else await api.post("/fleet/expenses", d);
            toast.success("Expense saved");
            setExpenseDlg(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const delExpense = async (row) => {
        if (!window.confirm("Delete this expense?")) return;
        try {
            await api.delete(`/fleet/expenses/${row.id}`);
            toast.success("Expense deleted");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const filteredVehicles = useMemo(() => {
        const needle = vehicleSearch.trim().toLowerCase();
        return vehicles.filter((v) => {
            if (vehicleStatus !== "all" && (v.status || "available") !== vehicleStatus) return false;
            if (vehicleCategory !== "all" && (v.vehicle_category || "vehicle") !== vehicleCategory) return false;
            if (!needle) return true;
            const blob = [v.vehicle_id, v.name, v.plate_no, v.vin, v.engine_no, v.barcode, v.assigned_farm].join(" ").toLowerCase();
            return blob.includes(needle);
        });
    }, [vehicles, vehicleSearch, vehicleStatus, vehicleCategory]);

    const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / pageSize));
    const pagedVehicles = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredVehicles.slice(start, start + pageSize);
    }, [filteredVehicles, page]);

    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const exportExcel = (rows, fileName) => {
        const sheet = XLSX.utils.json_to_sheet(rows || []);
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, "Fleet");
        XLSX.writeFile(book, fileName);
    };

    const exportPdf = (rows, title, fileName) => {
        const doc = new jsPDF();
        const cols = rows?.length ? Object.keys(rows[0]) : [];
        const body = (rows || []).map((r) => cols.map((c) => String(r[c] ?? "")));
        doc.text(title, 14, 14);
        autoTable(doc, { head: [cols], body, startY: 20, styles: { fontSize: 8 } });
        doc.save(fileName);
    };

    const k = summary.kpis || {};
    const health = k.fleet_health ?? 100;
    const notifications = summary.notifications || {};
    const expenseTotal = k.total_expenses ?? 0;

    return (
        <div className="space-y-6" data-testid="fleet-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-3"><Truck className="w-8 h-8 text-primary" />{t("nav.fleet", "Fleet Management")}</h1>
                    <p className="text-muted-foreground mt-1 text-sm">{i18n.t("final.099260f0b716")}</p>
                </div>
                <div className="flex gap-2">
                    {can("add") && <Button variant="outline" onClick={() => setFuelDlg({})} data-testid="new-fuel-btn"><Fuel className="w-4 h-4 mr-2" />{i18n.t("final.6197c35f640d")}</Button>}
                    {can("add") && <Button variant="outline" onClick={() => setMaintDlg({})} data-testid="new-maint-btn"><Wrench className="w-4 h-4 mr-2" />{i18n.t("final.149183406751")}</Button>}
                    {can("add") && <Button variant="outline" onClick={() => setExpenseDlg({})} data-testid="new-expense-btn"><FileText className="w-4 h-4 mr-2" />{i18n.t("final.f53d122b2016")}</Button>}
                    {can("add") && <Button onClick={() => setVehicleDlg({})} data-testid="new-vehicle-btn"><Plus className="w-4 h-4 mr-2" />{i18n.t("final.4130f658233f")}</Button>}
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="dashboard" data-testid="tab-dashboard">{i18n.t("final.d87f47b47e4d")}</TabsTrigger>
                    <TabsTrigger value="vehicles" data-testid="tab-vehicles">{i18n.t("final.1fd6a805dae7")}</TabsTrigger>
                    <TabsTrigger value="fuel" data-testid="tab-fuel">{i18n.t("final.0b645d1b6599")}</TabsTrigger>
                    <TabsTrigger value="maintenance" data-testid="tab-maint">{i18n.t("final.94de303bbef8")}</TabsTrigger>
                    <TabsTrigger value="expenses" data-testid="tab-expenses">{i18n.t("final.8d437dbf95ab")}</TabsTrigger>
                    <TabsTrigger value="reports" data-testid="tab-reports">{i18n.t("final.88bc3fe3dadd")}</TabsTrigger>
                    <TabsTrigger value="inspections" data-testid="tab-inspections">{i18n.t("final.0d7c240cba1c")}</TabsTrigger>
                </TabsList>

                <TabsContent value="dashboard" className="mt-4 space-y-5">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 gap-3">
                        <KpiCard icon={Truck} label={i18n.t("final.5bcce6c9362d")} value={formatNumber(k.total_vehicles || 0)} tone="primary" testid="kpi-total" />
                        <KpiCard icon={Activity} label={i18n.t("final.a733b809d2f1")} value={formatNumber(k.in_use || 0)} tone="blue" testid="kpi-active" />
                        <KpiCard icon={Wrench} label={i18n.t("final.255640665eb7")} value={formatNumber((k.repair || 0) + (k.under_maintenance || 0))} tone="rose" testid="kpi-repair" />
                        <KpiCard icon={CalendarClock} label={i18n.t("final.8a2783493a54")} value={formatNumber(k.service_upcoming || k.due_service || 0)} tone="amber" testid="kpi-due-service" />
                        <KpiCard icon={Fuel} label={i18n.t("final.117b44ac2067")} value={formatCurrency(k.fuel_cost || 0)} tone="primary" testid="kpi-fuel" />
                        <KpiCard icon={Wrench} label={i18n.t("final.4e4a56efd2f0")} value={formatCurrency(k.maintenance_cost || 0)} tone="amber" testid="kpi-maint" />
                        <KpiCard icon={FileText} label={i18n.t("final.750134b65d1b")} value={formatCurrency(expenseTotal)} tone="slate" testid="kpi-monthly" />
                        <KpiCard icon={ShieldCheck} label={i18n.t("final.ea3c24ad2473")} value={`${k.utilization || 0}%`} sub={`Health ${health}%`} tone="emerald" testid="kpi-util" />
                    </div>

                    <Card className="p-5">
                        <div className="font-semibold mb-3 flex items-center gap-2"><Fuel className="w-4 h-4" />{i18n.t("final.f4286ad9a793")}</div>
                        {(summary.fuel_trend || []).length === 0 ? (
                            <div className="text-sm text-muted-foreground">{i18n.t("final.83e2cb2108d1")}</div>
                        ) : (
                            <div className="h-56 min-h-[220px]">
                                <ResponsiveContainer>
                                    <LineChart data={summary.fuel_trend}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis dataKey="date" fontSize={10} />
                                        <YAxis fontSize={10} />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="cost" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                                        <Line type="monotone" dataKey="liters" stroke="hsl(var(--accent))" strokeWidth={2} dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </Card>

                    <Card className="p-5">
                        <div className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{i18n.t("final.753a22b2eb61")}</div>
                        <div className="grid md:grid-cols-3 gap-3 text-sm">
                            <div>{i18n.t("final.0f0ce6d6e190")} <b>{(notifications.registration_due || []).length}</b></div>
                            <div>{i18n.t("final.f700fcdb40d3")} <b>{(notifications.insurance_due || []).length}</b></div>
                            <div>{i18n.t("final.7600ac5e38ed")} <b>{(notifications.service_due || []).length}</b></div>
                            <div>{i18n.t("final.62a0ddfe7104")} <b>{(notifications.oil_change_due || []).length}</b></div>
                            <div>{i18n.t("final.d8f09bb951ba")} <b>{(notifications.tyre_replacement_due || []).length}</b></div>
                            <div>{i18n.t("final.674d8f64aa0b")} <b>{(notifications.battery_replacement_due || []).length}</b></div>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="vehicles" className="mt-4 space-y-4">
                    <Card className="p-4">
                        <div className="grid md:grid-cols-4 gap-2">
                            <div className="md:col-span-2 relative">
                                <Search className="absolute left-3 top-3.5 w-4 h-4 text-muted-foreground" />
                                <Input className="pl-9" placeholder={i18n.t("final.bd55c18395bf")} value={vehicleSearch} onChange={(e) => setVehicleSearch(e.target.value)} data-testid="fleet-search" />
                            </div>
                            <Select value={vehicleCategory} onValueChange={setVehicleCategory}>
                                <SelectTrigger data-testid="fleet-filter-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{i18n.t("final.1fd266c217d7")}</SelectItem>
                                    {VEHICLE_CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Select value={vehicleStatus} onValueChange={setVehicleStatus}>
                                <SelectTrigger data-testid="fleet-filter-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{i18n.t("final.9cb29e734a6c")}</SelectItem>
                                    {VEHICLE_STATUS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </Card>

                    {pagedVehicles.length === 0 ? (
                        <Card className="p-8 text-center text-muted-foreground">{i18n.t("final.c83f9e9d5b4d")}</Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {pagedVehicles.map((v) => {
                                const s = statusMeta(v.status);
                                const qrValue = v.qr_code || v.vehicle_id || v.id;
                                const barcodeValue = v.barcode || v.vehicle_id || v.id;
                                return (
                                    <Card key={v.id} className="p-4" data-testid={`vehicle-card-${v.id}`}>
                                        <div className="flex justify-between items-start gap-2">
                                            <div className="min-w-0">
                                                <div className="font-semibold text-lg truncate">{v.name}</div>
                                                <div className="text-xs text-muted-foreground">{categoryLabel(v.vehicle_category)} · {v.make || ""} {v.model || ""}</div>
                                                <div className="text-xs font-mono mt-1">{v.vehicle_id || v.id}</div>
                                            </div>
                                            <Badge className={`text-[10px] ${s.color}`} variant="outline">{s.label}</Badge>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                                            <div>{i18n.t("final.91cae9bcc20c")} <b>{v.plate_no || "-"}</b></div>
                                            <div>{i18n.t("final.f4d631a14799")} <b>{v.vin || "-"}</b></div>
                                            <div>{i18n.t("final.cbce6e1d7dcf")} <b>{v.fuel_type || "-"}</b></div>
                                            <div>{i18n.t("final.4edef537bfac")} <b>{formatNumber(v.current_odometer || 0)}</b></div>
                                            <div>{i18n.t("final.8b8b0e4621e0")} <b>{v.assigned_farm || "-"}</b></div>
                                            <div>{i18n.t("final.b5b7f6b7f66a")} <b>{employees.find((e) => e.id === v.assigned_driver_id)?.name || "-"}</b></div>
                                        </div>
                                        <div className="mt-3 grid grid-cols-2 gap-3 items-start">
                                            <div className="flex flex-col items-center p-2 border rounded-lg">
                                                <QrCode className="w-4 h-4 mb-1 text-muted-foreground" />
                                                <QRCodeSVG value={qrValue} size={58} />
                                            </div>
                                            <div className="p-2 border rounded-lg text-center">
                                                <Barcode className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                                                <div className="font-mono text-[10px] break-all">{barcodeValue}</div>
                                            </div>
                                        </div>
                                        {(v.photo_gallery || []).length > 0 && (
                                            <div className="mt-3 text-xs text-muted-foreground">{i18n.t("final.2376a0d7507c")} {(v.photo_gallery || []).length}</div>
                                        )}
                                        {can("edit") && (
                                            <div className="mt-3 flex gap-2">
                                                <Button size="sm" variant="outline" onClick={() => setVehicleDlg(v)} data-testid={`edit-vehicle-${v.id}`}><PencilLine className="w-3.5 h-3.5 mr-1" />{i18n.t("final.5301648dcf6b")}</Button>
                                                {can("delete") && <Button size="sm" variant="ghost" onClick={() => delVehicle(v)}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>}
                                            </div>
                                        )}
                                    </Card>
                                );
                            })}
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        <div className="text-xs text-muted-foreground">{i18n.t("final.fb06270f7c21")} {page} {i18n.t("final.de04fa0e29f9")} {totalPages} · {filteredVehicles.length} {i18n.t("final.86761b63a7bd")}</div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{i18n.t("final.50f94286ba30")}</Button>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{i18n.t("final.bc981983e7f5")}</Button>
                            <Button size="sm" variant="outline" onClick={() => exportExcel(filteredVehicles, "fleet-registry.xlsx")}>{i18n.t("final.4a245944efb6")}</Button>
                            <Button size="sm" variant="outline" onClick={() => exportPdf(filteredVehicles, "Fleet Registry", "fleet-registry.pdf")}>{i18n.t("final.3dd7d56a8ed3")}</Button>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="fuel" className="mt-4">
                    <Card>
                        <div className="p-4 border-b flex items-center justify-between"><div className="font-semibold">{i18n.t("final.1c8c444ef1fc")}</div><Button size="sm" onClick={() => setFuelDlg(true)}><Plus className="w-4 h-4 mr-1" />{i18n.t("final.6197c35f640d")}</Button></div>
                        <div className="divide-y">
                            {fuelLogs.map((f) => {
                                const v = vehicles.find((x) => x.id === f.vehicle_id);
                                return (
                                    <div key={f.id} className="p-4 flex items-center gap-3 text-sm" data-testid={`fuel-row-${f.id}`}>
                                        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary grid place-items-center"><Fuel className="w-4 h-4" /></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold truncate">{v?.name || "Unknown"}</div>
                                            <div className="text-xs text-muted-foreground">{f.date} · {f.liters}{i18n.t("final.15d42f2900c2")} {formatCurrency(f.price_per_l || 0)} {i18n.t("final.eefa183380c8")} {formatNumber(f.odometer || 0)} {i18n.t("final.55ef43bbccd7")} {formatNumber(f.engine_hours || 0)}</div>
                                        </div>
                                        <div className="font-semibold">{formatCurrency(f.total_cost || 0)}</div>
                                        {can("edit") && <Button size="sm" variant="outline" onClick={() => setFuelDlg(f)} data-testid={`edit-fuel-${f.id}`}><PencilLine className="w-3.5 h-3.5" /></Button>}
                                        {can("delete") && <Button size="sm" variant="ghost" onClick={() => delFuel(f)} data-testid={`delete-fuel-${f.id}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>}
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="maintenance" className="mt-4">
                    <Card>
                        <div className="p-4 border-b flex items-center justify-between"><div className="font-semibold">{i18n.t("final.c9b5eabe92c4")}</div><Button size="sm" onClick={() => setMaintDlg(true)}><Plus className="w-4 h-4 mr-1" />{i18n.t("final.149183406751")}</Button></div>
                        <div className="divide-y">
                            {maint.map((m) => {
                                const v = vehicles.find((x) => x.id === m.vehicle_id);
                                return (
                                    <div key={m.id} className="p-4 flex items-center gap-3 text-sm" data-testid={`maint-row-${m.id}`}>
                                        <div className="w-9 h-9 rounded-lg bg-amber-500/15 text-amber-700 grid place-items-center"><Wrench className="w-4 h-4" /></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold truncate">{v?.name || "Unknown"} · {(m.kind || "service").replace(/_/g, " ")}</div>
                                            <div className="text-xs text-muted-foreground">{m.date} {i18n.t("final.3430e769856a")} {formatCurrency(m.labor_cost || 0)} {i18n.t("final.ab6e3f083b35")} {m.next_due_date || "-"}</div>
                                            {m.parts_used && <div className="text-xs text-muted-foreground truncate">{i18n.t("final.1ef085c054e7")} {m.parts_used}</div>}
                                        </div>
                                        <div className="font-semibold">{formatCurrency(m.cost || 0)}</div>
                                        {can("edit") && <Button size="sm" variant="outline" onClick={() => setMaintDlg(m)} data-testid={`edit-maint-${m.id}`}><PencilLine className="w-3.5 h-3.5" /></Button>}
                                        {can("delete") && <Button size="sm" variant="ghost" onClick={() => delMaint(m)} data-testid={`delete-maint-${m.id}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>}
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="expenses" className="mt-4 space-y-4">
                    <Card className="p-4">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div>
                                <div className="font-semibold">{i18n.t("final.3142b38e900f")}</div>
                                <div className="text-xs text-muted-foreground">{i18n.t("final.bb56811cbaa0")}</div>
                            </div>
                            {can("add") && <Button onClick={() => setExpenseDlg({})}><Plus className="w-4 h-4 mr-1" />{i18n.t("final.f53d122b2016")}</Button>}
                        </div>
                    </Card>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {expenses.map((row) => {
                            const vehicle = vehicles.find((v) => v.id === row.vehicle_id);
                            const cat = EXPENSE_CATEGORIES.find((c) => c.key === row.category)?.label || row.category;
                            return (
                                <Card key={row.id} className="p-4" data-testid={`expense-row-${row.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-semibold truncate">{cat}</div>
                                            <div className="text-xs text-muted-foreground truncate">{vehicle?.name || "Unknown vehicle"} · {row.supplier || "No supplier"}</div>
                                        </div>
                                        <div className="font-semibold text-primary">{formatCurrency(row.amount || 0)}</div>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-3 space-y-1">
                                        <div>{i18n.t("final.81b7d2ea42df")} {row.date || "-"}</div>
                                        <div>{i18n.t("final.9c3befe7e22c")} {row.notes || "-"}</div>
                                        {row.receipt_url && <a href={row.receipt_url} target="_blank" rel="noreferrer" className="text-primary underline">{i18n.t("final.f6ce3b6fcfca")}</a>}
                                    </div>
                                    <div className="mt-3 flex gap-2">
                                        {can("edit") && <Button size="sm" variant="outline" onClick={() => setExpenseDlg(row)} data-testid={`edit-expense-${row.id}`}><PencilLine className="w-3.5 h-3.5 mr-1" />{i18n.t("final.5301648dcf6b")}</Button>}
                                        {can("delete") && <Button size="sm" variant="ghost" onClick={() => delExpense(row)} data-testid={`delete-expense-${row.id}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>}
                                    </div>
                                </Card>
                            );
                        })}
                        {expenses.length === 0 && <Card className="p-8 text-center text-muted-foreground">{i18n.t("final.5a2a2aa092fe")}</Card>}
                    </div>
                </TabsContent>

                <TabsContent value="reports" className="mt-4 space-y-3">
                    <Card className="p-4 flex flex-wrap gap-2 items-center">
                        <Select value={reportType} onValueChange={setReportType}>
                            <SelectTrigger className="w-64" data-testid="fleet-report-type"><SelectValue /></SelectTrigger>
                            <SelectContent>{REPORT_TYPES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button variant="outline" onClick={() => exportExcel(reportRows, `fleet-${reportType}.xlsx`)}>{i18n.t("final.4a245944efb6")}</Button>
                        <Button variant="outline" onClick={() => exportPdf(reportRows, `Fleet ${reportType}`, `fleet-${reportType}.pdf`)}>{i18n.t("final.3dd7d56a8ed3")}</Button>
                        <div className="text-xs text-muted-foreground">{i18n.t("final.5d08f9511d95")} {reportRows.length}</div>
                    </Card>
                    <Card className="p-4 overflow-auto">
                        {reportRows.length === 0 ? <div className="text-sm text-muted-foreground">{i18n.t("final.a994e25516c0")}</div> : (
                            <table className="w-full text-xs" data-testid="fleet-report-table">
                                <thead>
                                    <tr className="border-b">{Object.keys(reportRows[0]).map((k) => <th key={k} className="text-left py-2 pr-3">{k.replace(/_/g, " ")}</th>)}</tr>
                                </thead>
                                <tbody>
                                    {reportRows.map((r, i) => (
                                        <tr key={i} className="border-b border-border/40">{Object.keys(reportRows[0]).map((k) => <td key={k} className="py-2 pr-3">{String(r[k] ?? "")}</td>)}</tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="inspections" className="mt-4">
                    <Card className="p-5 space-y-3">
                        <div className="flex items-center gap-2 font-semibold"><ClipboardCheck className="w-4 h-4" />{i18n.t("final.e95921ab1183")}</div>
                        <div className="grid md:grid-cols-2 gap-3">
                            {inspectionTpls.map((tpl) => (
                                <Card key={tpl.id} className="p-4" data-testid={`insp-tpl-${tpl.code}`}>
                                    <div className="font-semibold">{tpl.title}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{tpl.description}</div>
                                    <Button asChild size="sm" className="mt-3"><a href="/forms">{i18n.t("final.f9543f047f25")}</a></Button>
                                </Card>
                            ))}
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>

            <VehicleDialog open={!!vehicleDlg} initial={vehicleDlg} employees={employees} onSave={saveVehicle} onClose={() => setVehicleDlg(null)} />
            <FuelDialog open={!!fuelDlg} initial={fuelDlg} vehicles={vehicles} onSave={saveFuel} onClose={() => setFuelDlg(null)} />
            <MaintDialog open={!!maintDlg} initial={maintDlg} vehicles={vehicles} onSave={saveMaint} onClose={() => setMaintDlg(null)} />
            <ExpenseDialog open={!!expenseDlg} initial={expenseDlg} vehicles={vehicles} onSave={saveExpense} onClose={() => setExpenseDlg(null)} />
        </div>
    );
}
